import React, { useEffect, useState } from 'react';
import { loadBookmarks } from '../../service/storage-service';
import IBookmark from '../../types/IBookmark';
import { Bookmark } from './bookmark/Bookmark';
import { ImportExport } from '../import-export/importExport';
import { Help } from '../help/Help';
import { FormInput } from '../elements/form/FormInput';
import { BookmarkDialog } from './add-bookmark/BookmarkDialog';
import { Modal } from '../elements/modal/Modal';
import Button from '../elements/button/Button';
import { getSharedBookmark, clearSharedTarget } from '../../utils/share-target';

interface Props {
  bookmarks: IBookmark[];
  onBookmarksChange: React.Dispatch<React.SetStateAction<IBookmark[]>>;
  menuOpen: boolean;
  onMenuClose: () => void;
}

export const Bookmarks: React.FC<Props> = ({
  bookmarks,
  onBookmarksChange,
  menuOpen,
  onMenuClose,
}) => {
  const setBookmarks = onBookmarksChange;
  const [searchText, setSearchText] = useState<string>('');
  const [bookmarkDialog, setBookmarkDialog] = useState<{
    title?: string;
    bookmark?: IBookmark;
    onOk?: (bookmark: IBookmark) => void;
  } | null>(null);
  const [bookmarkRemoveDialog, setBookmarkRemoveDialog] = useState<{
    show: boolean;
    title?: string;
    bookmark?: IBookmark;
  }>({
    show: false,
  });

  useEffect(() => {
    setBookmarks(loadBookmarks());
    const shared = getSharedBookmark();
    if (shared) {
      setBookmarkDialog({
        title: 'Add new',
        bookmark: { id: '', ...shared },
        onOk: addBookmark,
      });
      clearSharedTarget();
    }
  }, []);

  function addBookmark(bookmark: IBookmark) {
    if (bookmarks.some((item) => item.url === bookmark.url)) {
      alert('This bookmark already exists');
      return;
    }
    // Functional update: `bookmarks` may be a stale closure (share dialog).
    setBookmarks((prev) =>
      prev.some((item) => item.url === bookmark.url)
        ? prev
        : [...prev, bookmark],
    );
    hideBookmarkDialog();
  }

  function updateBookmark(bookmark: IBookmark) {
    setBookmarks((prev) => {
      const foundIndex = prev.findIndex(
        (item: IBookmark) => item.id === bookmark.id,
      );

      if (foundIndex === -1) {
        return prev;
      }

      const newBookmarks = [...prev];
      newBookmarks[foundIndex] = bookmark;
      return newBookmarks;
    });
    hideBookmarkDialog();
  }

  function showAddBookmarkDialog() {
    setBookmarkDialog({
      title: 'Add new',
      bookmark: null,
      onOk: addBookmark,
    });
  }

  function showEditBookmarkDialog(bookmark: IBookmark) {
    setBookmarkDialog({
      title: 'Edit',
      bookmark,
      onOk: updateBookmark,
    });
  }

  function hideBookmarkDialog() {
    setBookmarkDialog(null);
  }

  function showBookmarkRemoveConfirmation(bookmarkToRemove: IBookmark) {
    setBookmarkRemoveDialog({
      show: true,
      title: `Do you really want to remove '${bookmarkToRemove.title}'?`,
      bookmark: bookmarkToRemove,
    });
  }

  function hideBookmarkRemoveDialog() {
    setBookmarkRemoveDialog({ show: false, title: null, bookmark: null });
  }

  function removeBookmark() {
    const toRemove = bookmarkRemoveDialog.bookmark;
    setBookmarks((prev) => prev.filter((b) => b !== toRemove));
    hideBookmarkRemoveDialog();
  }

  function filteredBookmarks(text: string) {
    const lowerCaseText = text.toLowerCase();
    return bookmarks.filter(
      (bookmark: IBookmark) =>
        bookmark.title.toLowerCase().includes(lowerCaseText) ||
        bookmark.url.toLowerCase().includes(lowerCaseText),
    );
  }

  const actionItem = (className: string) => (
    <>
      <Button
        small
        icon="add"
        className={className}
        onClick={() => {
          showAddBookmarkDialog();
          onMenuClose();
        }}
      >
        <span>NEW</span>
      </Button>
      <ImportExport
        className={className}
        bookmarks={bookmarks}
        onBookmarksImport={setBookmarks}
      />
      <Help className={className} />
    </>
  );

  return (
    <div className="Bookmarks">
      <div className="level is-mobile is-hidden-mobile">
        <div className="level-right">{actionItem('level-item')}</div>
      </div>

      {menuOpen && (
        <div className="side-menu" role="menu">
          {actionItem('mb-2')}
        </div>
      )}
      {menuOpen && <div className="side-menu-backdrop" onClick={onMenuClose} />}

      <div className="control has-icons-left">
        <FormInput
          placeholder="Search..."
          type="search"
          onChange={(e) => setSearchText(e.target.value)}
        />
        <span className="icon is-small is-left">
          <i className="material-icons">search</i>
        </span>
      </div>

      {filteredBookmarks(searchText).map((bookmark) => (
        <Bookmark
          className="mt-1"
          key={bookmark.id}
          bookmark={bookmark}
          onEdit={() => showEditBookmarkDialog(bookmark)}
          onRemove={showBookmarkRemoveConfirmation}
        />
      ))}

      {bookmarkDialog && (
        <BookmarkDialog
          title={bookmarkDialog.title}
          bookmark={bookmarkDialog.bookmark}
          onSave={bookmarkDialog.onOk}
          onCancel={hideBookmarkDialog}
        />
      )}
      {bookmarkRemoveDialog && (
        <Modal
          show={bookmarkRemoveDialog.show}
          onOk={removeBookmark}
          onCancel={hideBookmarkRemoveDialog}
        >
          {bookmarkRemoveDialog.title}
        </Modal>
      )}
    </div>
  );
};
